import React, { createContext, useContext } from 'react';

import { useEditorRef, useFocused } from 'platejs/react';
import type { RenderPlaceholderProps } from 'platejs/react';
import { makeStyles } from 'tss-react/mui';

import {
  EMPTY_PARAGRAPH_ACTIONS_WIDTH,
  useEmptyParagraphActionsEnabled,
} from './empty-paragraph-actions';
import { MentionEntitiesContext } from './plugins/mention-entity-kit';
import { DEFAULT_ENABLED_TYPES } from './plugins/mention-entity-picker-helpers';
import { getHasSkillPicker } from './plugins/skill-element-plugin';

// Whether the editor asked for its commands as the placeholder.
const CommandPlaceholderContext = createContext(false);
export const CommandPlaceholderProvider = CommandPlaceholderContext.Provider;

const useStyles = makeStyles()({
  placeholder: {
    display: 'block',
    fontStyle: 'normal',
    fontWeight: 'normal',
    overflow: 'hidden',
    textDecoration: 'none',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const PlaceholderText = React.memo(function PlaceholderText({
  attributes,
  children,
  testId,
}: RenderPlaceholderProps & { testId?: string }) {
  const { classes } = useStyles();
  const focused = useFocused();
  const actionsEnabled = useEmptyParagraphActionsEnabled();
  // The empty-paragraph actions share this line only while the editor has focus.
  const reserveActionsSpace = focused && actionsEnabled;

  return (
    <span
      {...attributes}
      className={classes.placeholder}
      data-testid={testId}
      style={{
        ...attributes.style,
        maxWidth: reserveActionsSpace ? `calc(100% - ${EMPTY_PARAGRAPH_ACTIONS_WIDTH}px)` : '100%',
      }}
    >
      {children}
    </span>
  );
});

const CommandPlaceholder = React.memo(function CommandPlaceholder(props: RenderPlaceholderProps) {
  const editor = useEditorRef();
  const hasSkillPicker = getHasSkillPicker(editor);
  const mentions = useContext(MentionEntitiesContext);
  const hasMentions = Boolean(
    mentions && (mentions.enabledTypes ?? DEFAULT_ENABLED_TYPES).length > 0
  );
  if (!hasMentions && !hasSkillPicker) return <PlaceholderText {...props} />;

  return (
    <PlaceholderText attributes={props.attributes} testId="editor-command-placeholder">
      Type {hasMentions && '@ to mention'}
      {hasMentions && hasSkillPicker && ' or '}
      {hasSkillPicker && '/ to use a skill'}...
    </PlaceholderText>
  );
});

/**
 * An editable editor that asks for it uses the commands it takes as its placeholder, since
 * `/` is otherwise invisible. Every other editor, such as a comment box, shows the caller's
 * text.
 */
const EditorPlaceholder = React.memo(function EditorPlaceholder(props: RenderPlaceholderProps) {
  const actionsEnabled = useEmptyParagraphActionsEnabled();
  const commandPlaceholder = useContext(CommandPlaceholderContext);
  if (commandPlaceholder && actionsEnabled) return <CommandPlaceholder {...props} />;

  return <PlaceholderText {...props} />;
});

/**
 * Renders the placeholder of every `AlineMarkdownEditor`. Passed to the editable as
 * `renderPlaceholder`, so it must stay one stable function.
 */
export function renderEditorPlaceholder(props: RenderPlaceholderProps) {
  return <EditorPlaceholder {...props} />;
}
